import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  lazyRouteComponent,
  Outlet
} from '@tanstack/react-router'
import { useAuthStore } from '@/stores/authStore'
import { hasPermission } from '@/utils/helpers'
import type { QueryClient } from '@tanstack/react-query'

// Context tipado para o router
interface RouterContext {
  queryClient: QueryClient
}

// Root route
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
})

// ===== PUBLIC ROUTES (sem auth) =====

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('@/pages/Login'), 'Login'),
})

const taskActionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/task-action/$taskId/$token',
  component: lazyRouteComponent(() => import('@/pages/TaskActionPage'), 'default'),
})

const financeiroDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/financeiro/dashboard',
  component: lazyRouteComponent(() => import('@/pages/FinancialDashboardPage'), 'default'),
})

const publicDbaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/public/dba-request',
  component: lazyRouteComponent(() => import('@/pages/PublicDbaRequest'), 'default'),
})

// ===== PROTECTED LAYOUT (auth guard UMA VEZ) =====

const protectedLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: async ({ location }) => {
    const { loading } = useAuthStore.getState()

    // Aguardar loading terminar antes de decidir
    if (loading) {
      await new Promise<void>((resolve) => {
        const unsub = useAuthStore.subscribe(
          (state) => state.loading,
          (isLoading) => {
            if (!isLoading) { unsub(); resolve() }
          }
        )
      })
    }

    const state = useAuthStore.getState()
    if (!state.isAuthenticated || !state.user) {
      // Salvar path para redirect-after-login
      useAuthStore.getState().setRedirectAfterLogin(window.location.pathname)
      throw redirect({ to: '/login' })
    }

    // Restricao de rota por identidade (DBA so acessa /dba/*) — NAO e permissao
    // hierarquica (usePermission). beforeLoad nao pode usar hooks, por isso le direto.
    if (state.profile?.role === 'DBA' && !location.pathname.startsWith('/dba')) {
      throw redirect({ to: '/dba' as string })
    }
  },
  component: lazyRouteComponent(() => import('@/components/layout/MainLayout'), 'MainLayout'),
})

// Helper para rotas que requerem GERENTE
const gerenteGuard = async () => {
  const { profile } = useAuthStore.getState()
  if (!profile || !hasPermission(profile.role ?? '', 'GERENTE')) {
    throw redirect({ to: '/dashboard' })
  }
}

// ===== PROTECTED ROUTES (code splitting via lazyRouteComponent) =====

const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/dashboard',
  component: lazyRouteComponent(() => import('@/pages/Dashboard'), 'Dashboard'),
})

const companiesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/companies',
  component: lazyRouteComponent(() => import('@/pages/Companies'), 'Companies'),
})

const companyDetailsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/companies/$id',
  component: lazyRouteComponent(() => import('@/pages/Companies/CompanyDetails'), 'CompanyDetails'),
})

const usersRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/users',
  beforeLoad: gerenteGuard,
  component: lazyRouteComponent(() => import('@/pages/Users'), 'Users'),
})

const userDetailsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/users/$userId',
  beforeLoad: gerenteGuard,
  component: lazyRouteComponent(() => import('@/pages/Users/UserDetails'), 'UserDetails'),
})

const atendimentoRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/atendimento',
  component: lazyRouteComponent(() => import('@/pages/AtendimentoFullscreen'), 'AtendimentoFullscreen'),
})

const whatsappRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/whatsapp',
  component: lazyRouteComponent(() => import('@/pages/WhatsApp'), 'WhatsAppPage'),
})

const whatsappAttendanceRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/whatsapp-attendance',
  component: lazyRouteComponent(() => import('@/modules/whatsapp-center/WhatsAppCenter'), 'WhatsAppCenter'),
})

const whatsappSettingsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/settings/whatsapp',
  component: lazyRouteComponent(() => import('@/pages/Settings/WhatsAppSettings'), 'WhatsAppSettings'),
})

const quickMessagesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/settings/quick-messages',
  component: lazyRouteComponent(() => import('@/pages/Settings/QuickMessages'), 'QuickMessages'),
})

const tasksRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/tasks',
  component: lazyRouteComponent(() => import('@/pages/Tasks'), 'Tasks'),
})

// DBA com search params tipados
const dbaRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/dba',
  validateSearch: (search: Record<string, unknown>) => ({
    task: (search.task as string) || undefined,
  }),
  component: lazyRouteComponent(() => import('@/pages/Dba'), 'Dba'),
})

const dbaTaskDetailsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/dba/$taskId',
  component: lazyRouteComponent(() => import('@/pages/DbaTaskDetails'), 'DbaTaskDetails'),
})

const discountsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/discounts',
  validateSearch: (search: Record<string, unknown>) => ({
    companyId: (search.companyId as string) || undefined,
  }),
  component: lazyRouteComponent(() => import('@/pages/Discounts'), 'Discounts'),
})

const discountAnalyticsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/discounts/analytics',
  component: lazyRouteComponent(() => import('@/pages/DiscountAnalytics'), 'DiscountAnalytics'),
})

const discountLimitsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/discounts/limits',
  beforeLoad: gerenteGuard,
  component: lazyRouteComponent(() => import('@/pages/DiscountLimits'), 'DiscountLimits'),
})

const discountSettingsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/discounts/settings',
  beforeLoad: gerenteGuard,
  component: lazyRouteComponent(() => import('@/pages/DiscountSettings'), 'DiscountSettingsPage'),
})

const negotiationsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/negotiations',
  validateSearch: (search: Record<string, unknown>) => ({
    companyId: (search.companyId as string) || undefined,
  }),
  component: lazyRouteComponent(() => import('@/pages/Negotiations'), 'Negotiations'),
})

const negotiationDetailsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/negotiations/$id',
  component: lazyRouteComponent(() => import('@/pages/Negotiations/NegotiationDetails'), 'NegotiationDetails'),
})

const improvementsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/improvements',
  component: lazyRouteComponent(() => import('@/pages/Improvements'), 'Improvements'),
})

const newImprovementRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/improvements/new',
  component: lazyRouteComponent(() => import('@/pages/Improvements/New'), 'NewImprovement'),
})

const improvementDetailsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/improvements/$id',
  component: lazyRouteComponent(() => import('@/pages/Improvements/Details'), 'ImprovementDetailsPage'),
})

const editImprovementRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/improvements/$id/edit',
  component: lazyRouteComponent(() => import('@/pages/Improvements/Edit'), 'EditImprovement'),
})

const financeiroRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/financeiro',
  component: lazyRouteComponent(() => import('@/pages/Financeiro'), 'Financeiro'),
})

// Certificados
const certificadosCasaDosDadosRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/casa-dos-dados',
  component: lazyRouteComponent(() => import('@/pages/CertificadosCasaDosDados'), 'CertificadosCasaDosDados'),
})

const certificadosLeadsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/leads',
  component: lazyRouteComponent(() => import('@/pages/CertificadosLeads'), 'CertificadosLeads'),
})

const certificadosProdutosRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/produtos',
  component: lazyRouteComponent(() => import('@/pages/ProdutosCertificados'), 'ProdutosCertificados'),
})

const certificadosContabilidadesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/contabilidades',
  component: lazyRouteComponent(() => import('@/pages/ContabilidadesCertificados'), 'ContabilidadesCertificados'),
})

const certificadosParceirosRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/parceiros',
  component: lazyRouteComponent(() => import('@/pages/CertificadosParceiros'), 'CertificadosParceiros'),
})

const certificadosAtendimentoRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/atendimento',
  component: lazyRouteComponent(
    () => import('@/modules/certificados-whatsapp-center/CertificadosWhatsAppCenter'),
    'CertificadosWhatsAppCenter'
  ),
})

const certificadosCalendarioRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/calendario',
  component: lazyRouteComponent(() => import('@/pages/CalendarioCertificados'), 'default'),
})


const certificadosPdvRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/pdv',
  component: lazyRouteComponent(() => import('@/pages/PdvCertificados'), 'PdvCertificados'),
})
// Parceiros
const parceirosContabilidadesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/parceiros/contabilidades',
  component: lazyRouteComponent(() => import('@/pages/ContabilidadesCertificados'), 'ContabilidadesCertificados'),
})

const parceirosEmpresasRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/parceiros/empresas',
  component: lazyRouteComponent(() => import('@/pages/CertificadosParceiros'), 'CertificadosParceiros'),
})

const parceirosClientesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/parceiros/clientes',
  component: lazyRouteComponent(() => import('@/pages/ClientesParceiros'), 'ClientesParceiros'),
})

const parceirosContabilidadesDetailRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/parceiros/contabilidades/$id',
  component: lazyRouteComponent(() => import('@/pages/ContabilidadesCertificados/ContabilidadeDetail'), 'ContabilidadeDetail'),
})

const parceirosEmpresasDetailRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/parceiros/empresas/$id',
  component: lazyRouteComponent(() => import('@/pages/CertificadosParceiros/ParceiroEmpresaDetail'), 'ParceiroEmpresaDetail'),
})

const parceirosClientesDetailRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/parceiros/clientes/$id',
  component: lazyRouteComponent(() => import('@/pages/ClientesParceiros/ClienteDetail'), 'ClienteDetail'),
})

const certificadosClientesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/clientes',
  component: lazyRouteComponent(() => import('@/pages/ClientesCertificados'), 'ClientesCertificados'),
})

const certificadosClientesDetailRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/certificados/clientes/$id',
  component: lazyRouteComponent(() => import('@/pages/ClientesCertificados/ClienteDetail'), 'ClienteDetail'),
})

// Comercial
const vendasLeadsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/comercial/leads',
  component: lazyRouteComponent(() => import('@/pages/Vendas/Leads'), 'Leads'),
})

const vendasCasaDosDadosRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/comercial/casa-dos-dados',
  component: lazyRouteComponent(() => import('@/pages/Vendas/CasaDosDados'), 'CasaDosDados'),
})

const vendasAtendimentoRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/comercial/atendimento',
  component: lazyRouteComponent(
    () => import('@/modules/vendas-whatsapp-center/VendasWhatsAppCenter'),
    'VendasWhatsAppCenter'
  ),
})

const vendasLeadsAutocomRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/comercial/leads-autocom',
  component: lazyRouteComponent(() => import('@/pages/Vendas/LeadsAutocom'), 'LeadsAutocom'),
})

const vendasFollowUpRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/comercial/follow-up',
  component: lazyRouteComponent(() => import('@/pages/Vendas/FollowUp'), 'FollowUp'),
})

// ===== CATCH-ALL =====

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: lazyRouteComponent(() => import('@/pages/NotFound'), 'NotFoundPage'),
})

// Index redirect
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    const { isAuthenticated, profile } = useAuthStore.getState()
    if (isAuthenticated) {
      // Redirect pos-login por identidade exata (DBA -> /dba) — nao e permissao hierarquica
      if (profile?.role === 'DBA') {
        throw redirect({ to: '/dba' as string })
      }
      throw redirect({ to: '/dashboard' })
    } else {
      throw redirect({ to: '/login' })
    }
  },
})

// ===== ROUTE TREE =====

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  taskActionRoute,
  financeiroDashboardRoute,
  publicDbaRoute,
  notFoundRoute,
  protectedLayout.addChildren([
    dashboardRoute,
    companiesRoute, companyDetailsRoute,
    usersRoute, userDetailsRoute,
    atendimentoRoute,
    whatsappRoute, whatsappAttendanceRoute, whatsappSettingsRoute, quickMessagesRoute,
    tasksRoute,
    dbaRoute, dbaTaskDetailsRoute,
    discountsRoute, discountAnalyticsRoute, discountLimitsRoute, discountSettingsRoute,
    negotiationsRoute, negotiationDetailsRoute,
    improvementsRoute, newImprovementRoute, improvementDetailsRoute, editImprovementRoute,
    financeiroRoute,
    certificadosCasaDosDadosRoute, certificadosLeadsRoute,
    certificadosProdutosRoute, certificadosContabilidadesRoute, certificadosParceirosRoute,
    certificadosAtendimentoRoute, certificadosCalendarioRoute, certificadosPdvRoute,
    parceirosContabilidadesRoute, parceirosContabilidadesDetailRoute,
    parceirosEmpresasRoute, parceirosEmpresasDetailRoute,
    parceirosClientesRoute, parceirosClientesDetailRoute,
    certificadosClientesRoute, certificadosClientesDetailRoute,
    vendasLeadsRoute, vendasCasaDosDadosRoute, vendasAtendimentoRoute, vendasLeadsAutocomRoute, vendasFollowUpRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  context: { queryClient: undefined! }, // Injetado no App.tsx
})

// Type-safe router para uso em hooks
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
